import { HttpApiService } from "@/shared/services/httpApi.service";
import type { PaginatedResponse } from "@/shared/types/pagination.types";

import { PAYMENT_INCIDENTS_ENDPOINTS } from "../constants/paymentIncidents.constants";
import type {
  CreateIncidentRefundPayload,
  CreatePaymentIncidentEventPayload,
  CreatePaymentIncidentPayload,
  PaymentIncident,
  PaymentIncidentDetail,
  PaymentIncidentRefundCreated,
  PaymentIncidentReport,
  PaymentIncidentsSummary,
} from "../types/paymentIncidents.types";

const TWO_FACTOR_HEADER = "x-two-factor-code";

/**
 * HTTP client of the payment incidents (staff). Reads are open to staff with `payments:read`; every change is
 * for the `WALLET_TOP_UP_ADMIN_EMAILS` staff, and copying the report and refunding also carry the TOTP.
 */
export class PaymentIncidentsService {
  static async get(id: string): Promise<PaymentIncidentDetail> {
    const res = await HttpApiService.get<PaymentIncidentDetail>(PAYMENT_INCIDENTS_ENDPOINTS.byId(id));
    return res.data;
  }

  static async summary(): Promise<PaymentIncidentsSummary> {
    const res = await HttpApiService.get<PaymentIncidentsSummary>(PAYMENT_INCIDENTS_ENDPOINTS.SUMMARY);
    return res.data;
  }

  /** The incident open on an operation, if any. */
  static async findByOperation(operationId: string): Promise<PaymentIncident | null> {
    const res = await HttpApiService.get<PaginatedResponse<PaymentIncident>>(
      PAYMENT_INCIDENTS_ENDPOINTS.LIST,
      {
        params: { operation_id: operationId, limit: 1 },
      },
    );
    return res.data.data[0] ?? null;
  }

  /**
   * Opens it as `pending_report`. A payout that already has one answers 409 `incident_already_exists`; the
   * error body carries only the `code`, so the caller finds that incident with `findByOperation`.
   */
  static async create(payload: CreatePaymentIncidentPayload): Promise<PaymentIncidentDetail> {
    const res = await HttpApiService.post<PaymentIncidentDetail>(PAYMENT_INCIDENTS_ENDPOINTS.LIST, payload);
    return res.data;
  }

  /** The text for the provider. The first copy marks it reported. Behind the TOTP. */
  static async report(id: string, twoFactorCode: string): Promise<PaymentIncidentReport> {
    const res = await HttpApiService.post<PaymentIncidentReport>(
      PAYMENT_INCIDENTS_ENDPOINTS.report(id),
      {},
      { headers: { [TWO_FACTOR_HEADER]: twoFactorCode } },
    );
    return res.data;
  }

  /** A timeline entry, as `multipart/form-data` so files ride along under `attachments`. */
  static async addEvent(
    id: string,
    payload: CreatePaymentIncidentEventPayload,
  ): Promise<PaymentIncidentDetail> {
    const body = new FormData();
    body.append("action", payload.action);
    if (payload.note?.trim()) body.append("note", payload.note.trim());
    if (payload.provider_case_reference?.trim()) {
      body.append("provider_case_reference", payload.provider_case_reference.trim());
    }
    if (payload.occurred_at) body.append("occurred_at", payload.occurred_at);
    if (payload.resolution) body.append("resolution", payload.resolution);
    for (const file of payload.attachments ?? []) body.append("attachments", file, file.name);
    const res = await HttpApiService.post<PaymentIncidentDetail>(
      PAYMENT_INCIDENTS_ENDPOINTS.events(id),
      body,
    );
    return res.data;
  }

  /** Credits the client back (a wallet top-up of kind `incident_refund`). Behind the TOTP. */
  static async refund(
    id: string,
    payload: CreateIncidentRefundPayload,
    twoFactorCode: string,
  ): Promise<PaymentIncidentRefundCreated> {
    const res = await HttpApiService.post<PaymentIncidentRefundCreated>(
      PAYMENT_INCIDENTS_ENDPOINTS.clientRefund(id),
      payload,
      { headers: { [TWO_FACTOR_HEADER]: twoFactorCode } },
    );
    return res.data;
  }
}
